<template lang="pug">
  nav.navbar.navbar-expand-xl.navbar-dark.fixed-top.bg-dark.p-0(:class='sessionGetDisplayClass("nav")', ref='navBar')
    .banner-dev(v-if='showDevBanner') DEVELOPMENT

    .container-fluid
      font-awesome-icon.mx-3.ml-xl-0.hamburger(:icon='["fas", "bars"]', size='1x', @click='clickedHamburger')
      router-link.navbar-brand.navbar-brand-image.mr-2(to="/home")
        img(src='@assets/img/UH-logo.svg', alt='UH System')
      router-link.navbar-brand.d-none.d-sm-block(to='/home', :class='sessionGetDisplayClass("content")', @click.native='scrollToTop')
        h5.my-0
          .appName DATA.HAWAII.EDU
      .col.px-0.mr-3.mr-sm-0
        VueTypeaheadBootstrap.nav-search-bar(v-model='shortcutFilter',
                              :data='reports',
                              :showOnFocus='true',
                              placeholder='Search for a report or page',
                              @hit='hitShortCut',
                              :serializer='option => option.label',
                              tagsSearchField='tags',
                              searchTermField='search',
                              searchStringToken=' ',
                              :windowWidth='sessionWindowWidth',
                              :shouldBeFullWidth='!sessionSideBarShouldShiftContent',
                              inputClass='searchbar',
                              :isDark='sessionDisplayIsDark',
                              data-coachmark='0',
                              data-coachmark-text='Type in keywords or any terms related to the data or report that you are looking for. e.g. "enrollment" will show reports and page related to enrollment',
                              data-coachmark-type='navbar')
          template(v-if='shortcutFilter && shortcutFilter.length > 0', slot='append')
            button.btn.border-left-0.border.bg-white(type='button', @click='shortcutFilter = ""')
              span(aria-hidden='true') &times;
          template(slot='suggestion', slot-scope='{ data, highlightedTags, htmlText }')
            div(v-html='data.htmlLabel || htmlText')
            div
              template(v-for='(keyword, index) in highlightedTags')
                span(:class='index > 0 ? "ml-2" : ""')
                  font-awesome-icon.mr-1(:icon='["fas", "tag"]')
                span(v-html='keyword')
            .font-italic(v-if='data.htmlDesc', v-html='data.htmlDesc')

      .col-auto.d-none.d-sm-block.px-0
        .nav-item(v-if='sessionIsLoggedIn')
          button.btn.dropdown-toggle#dropdownMenuButton.nav-link(type="button", data-toggle="dropdown", aria-haspopup="true", aria-expanded="false")
            font-awesome-icon.icon.mx-1(:icon='["fa", "user"]')
          .dropdown-menu.dropdown-menu-md-right(aria-labelledby="dropdownMenuButton", :class='sessionGetDisplayClass("dropdown-menu")')
            a.dropdown-item.px-3 Logged in as {{ userFirstName }}
              img.mb-1.ml-1(v-if='isStaff', src='@assets/img/verified.png', title='IRAPO Staff 😉')
            a.dropdown-item.px-3 ISAT Compliant
              font-awesome-icon.mx-1(:icon='getISatComplianceIcon', :class='getISatComplianceColor', :title='getISatComplianceTitle')
              font-awesome-icon.mx-1(v-if='!sessionIsISatCompliant', :icon='["fas","info-circle"]', @click='doISatInfoClick')
            .dropdown-divider(role='separator')

            .custom-control.custom-switch.nav-content.ml-3(:class='sessionGetDisplayClass("content")')
              input.custom-control-input#displayModeSwitch(type='checkbox', v-model='selectedDisplayMode', @click='changeDisplayMode')
              label.custom-control-label(for='displayModeSwitch')
                font-awesome-icon.mx-1.text-warning(v-if='sessionDisplayIsDark', :icon='["fas", "sun"]')
                font-awesome-icon.mx-1.text-warning(v-else, :icon='["fas", "moon"]')
              span.badge.badge-success.ml-2 Beta

            router-link.nav-link.px-3(v-if='sessionHasRole(getAdminRole)', to='/admin/dashboard', data-toggle='collapse', data-target='.navbar-collapse.show', @click.native='scrollToTop') Admin Dashboard
            router-link.nav-link.px-3(v-if='sessionHasRole("irapo-maps-mapsmobile")', to='/mobile', data-toggle='collapse', data-target='.navbar-collapse.show', @click.native='scrollToTop') Mobile App

            a.dropdown-item.nav-link.px-3(@click='doLogout') Logout

        .nav-item(v-else)
          a.nav-link#nav-login(@click='doLogin') LOGIN
</template>

<script>
import { concat, debounce, flatten, filter, find, isEmpty, map } from 'lodash';
import { mapGetters } from 'vuex';
const _ = { concat, debounce, flatten, filter, find, isEmpty, map };
import eventBus from '@api/eventBus';
import oidcClient from '@api/oidcClient';
import analytics from '@api/analytics';
import util from '@api/util';
import VueTypeaheadBootstrap from 'vue-typeahead-bootstrap';
import Coachmark from '@api/coachmark';
import storage from '@api/storage';

const SIDEBAR_MENU_CLOSED_OFFSET = '0px';
const DELAY_MS = 100;
const DEBOUNCE_DELAY_MS = 1000;

const TIMES_TIL_SHOW_HELP = 10;
const SHOW_HELP_DELAY_MS = 500;
const SEEN_SEARCHBAR_TOUR = 'SEEN_SEARCHBAR_TOUR';

export default {
  name: 'NavBar',
  components: {
    VueTypeaheadBootstrap
  },
  data: () => ({
    selectedDisplayMode: null,
    shortcutFilter: '',
    tempShortcutFilter: '' // keep track of what was entered for logging
  }),
  computed: {
    ...mapGetters('content', [
      'environment',
      'tableOfContents'
    ]),
    ...mapGetters('session', [
      'session',
      'sessionDisplayIsDark',
      'sessionGetDisplayClass',
      'sessionHasRole',
      'sessionProperty',
      'sessionHasToken',
      'sessionIsISatCompliant',
      'sessionIsLoggedIn',
      'sessionWindowWidth',
      'sessionSideBarShouldShiftContent'
    ]),
    getAdminRole: function () {
      return util.ROLE_ADMIN_DEBUG;
    },
    userFirstName: function () {
      return this.sessionProperty('given_name') || this.sessionProperty('preferred_username');
    },
    showDevBanner: function () {
      return this.environment.ENVIRONMENT === 'test';
    },
    getISatComplianceIcon: function () {
      return this.sessionIsISatCompliant ? ['far', 'check-circle'] : ['fas', 'exclamation-triangle'];
    },
    getISatComplianceColor: function () {
      return this.sessionIsISatCompliant ? 'text-success' : 'text-danger';
    },
    getISatComplianceTitle: function () {
      return this.sessionIsISatCompliant ? '' : 'ISAT compliance record not found';
    },
    reports: function () {
      let allNavData = this.tableOfContents.reportData;
      if (this.tableOfContents.siteData) {
        allNavData = _.concat(this.tableOfContents.reportData, this.tableOfContents.siteData);
      }
      const flattenedReports = _.flatten(_.map(allNavData, (module) => {
        const toReturn = _.flatten(_.map(module.groups, group => group.reports));
        return _.concat(toReturn, module.reports || []);
      }));
      return _.map(_.filter(flattenedReports, rep => !rep.hideSearch), (report) => {
        // search field includes id, name, desc, and report keywords
        const toReturn = {
          id: report.id,
          search: `${report.id} | ${report.name} | ${report.desc} | ${(report.keywords || []).join('|')}`,
          label: `${report.id}: ${report.name}`,
          tags: report.keywords,
          description: report.desc
        };

        if (report.external) {
          toReturn.external = report.external;
        }
        if (report.link) {
          toReturn.link = report.link;
        }
        if (report.type) {
          toReturn.routerLink = {
            name: 'report',
            params: {
              reportId: report.id
            }
          }
        }
        return toReturn;
      });
    },
    isStaff: function () {
      return storage.getBoolean(storage.IS_IRAPO);
    }
  },
  methods: {
    changeDisplayMode: async function () {
      const targetMode = !this.selectedDisplayMode ? 'dark' : 'light';
      await this.$store.dispatch('session/setDisplayMode', targetMode);
      analytics.event(analytics.events.USER_ENGAGEMENT, {
        event_name: 'displayMode',
        value: targetMode
      });
    },
    doLogin: function () {
      oidcClient.signInPopup();
    },
    doLogout: function () {
      oidcClient.signoutPopup();
    },
    scrollToTop: function () {
      $('html,body').scrollTop(0);
    },
    doISatInfoClick: function () {
      eventBus.bus.$emit(eventBus.CHANNEL_BUS_MESSAGE, {
        message: eventBus.MESSAGES.ISAT_MODAL
      });
    },
    toggleSidebar: function () {
      const openOffset = `${util.SIDEBAR_WIDTH}px`;

      let isNowOpen = false;
      if (this.$refs.navBar.style.left === openOffset) {
        this.$refs.navBar.style.left = SIDEBAR_MENU_CLOSED_OFFSET;
      }
      else {
        isNowOpen = true;
        if (this.sessionSideBarShouldShiftContent) {
          this.$refs.navBar.style.left = openOffset;
        }
      }

      // set it in the store
      this.$store.dispatch('session/setSessionIsSideBarOpen', isNowOpen);

      analytics.event(analytics.events.USER_ENGAGEMENT, {
        event_name: 'toggleSidebar',
        value: isNowOpen ? 'open' : 'close'
      });
    },
    clickedHamburger: function () {
      eventBus.bus.$emit(eventBus.CHANNEL_TOGGLE_SIDEBAR);
    },
    hitShortCut: function () {
      const shortcut = _.find(this.reports, { label: this.shortcutFilter });
      if (shortcut) {
        if (shortcut.routerLink) {
          try {
            this.$router.push(shortcut.routerLink);
          }
          catch (err) {
            console.log(err);
          }
        }
        else if (shortcut.external && shortcut.link) {
          window.open(shortcut.link);
        }
        else if (shortcut.link) {
          this.$router.push(shortcut.link.replace('#', ''));
        }

        analytics.event(analytics.events.SELECT_ITEM, {
          item_list_id: 'search_bar',
          item_list_name: this.tempShortcutFilter,
          items: [{
            item_id: shortcut.id,
            item_name: shortcut.label
          }]
        });

        // clear out the filter after a timeout.  for some reason, setting it to empty right away
        // will cause it to match on the first result a send a second hit event
        setTimeout(() => {
          this.shortcutFilter = '';
          this.tempShortcutFilter = '';
        }, DELAY_MS);
      }
    },
    onSearchBarChange: function () {
      this.tempShortcutFilter = this.shortcutFilter;

      if (!_.isEmpty(this.shortcutFilter)) {
        analytics.event(analytics.events.SEARCH, {
          search_term: this.shortcutFilter.trim()
        });
      }
    }
  },
  created: function () {
    // we have 3 states but just testing with light/dark now
    this.selectedDisplayMode = this.sessionDisplayIsDark;

    eventBus.bus.$on(eventBus.CHANNEL_TOGGLE_SIDEBAR, this.toggleSidebar);

    eventBus.bus.$on(eventBus.CHANNEL_CLICKED_HELP, (options) => {
      const tutorialType = 'navbar'
      if (options.type === tutorialType) {
        this.scrollToTop();
        Coachmark().init({
          lockScroll: true,
          type: tutorialType
        });
        storage.setItem(SEEN_SEARCHBAR_TOUR, 'true');
      }
    });
  },
  mounted: function () {
    const seenSearchTour = false;//storage.getBoolean(SEEN_SEARCHBAR_TOUR);
    const timesVisited = parseFloat(storage.getItem(storage.NAV_VISITED) || 0);
    if (!seenSearchTour) {
      if (timesVisited >= TIMES_TIL_SHOW_HELP) {
        setTimeout(() => {
          eventBus.bus.$emit(eventBus.CHANNEL_TOGGLE_HELP, {});
        }, SHOW_HELP_DELAY_MS);
      }
      else {
        storage.setItem(storage.NAV_VISITED, timesVisited + 1);
      }
    }
  },
  beforeDestroy: function () {
    eventBus.bus.$off(eventBus.CHANNEL_TOGGLE_SIDEBAR, this.toggleSidebar);
    eventBus.bus.$off(eventBus.CHANNEL_CLICKED_HELP, this.toggleSidebar);
  },
  watch: {
    'shortcutFilter': {
      handler: _.debounce(function () {
        this.onSearchBarChange();
      }, DEBOUNCE_DELAY_MS)
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="stylus" scoped>
  @import '../assets/app.styl'

  .banner-dev
    position absolute
    top 0px
    right 10px
    color white
    background red
    width 50%
    text-align right
    padding-right 10px

  .navbar-brand-image
    margin-right 0
    img
      width 50px
      height 50px

  .dropdown-menu
    background-color dark-background-color
  .appName
    color white
    font-weight bold
  .title
    color highlight-color

  .nav-item
    cursor pointer
    .dropdown-item
      color white
      background-color transparent
    .nav-link
      color white
      background-color transparent
      &.main-active, &.router-link-active, &:hover
        color highlight-color

  .nav-content
    color white

  nav
    transition left .5s

  .hamburger
    color white
    cursor pointer
</style>
